import React, { useEffect, useState } from 'react';
import {
  Badge, Box, Button, Divider, FormControl, FormLabel, HStack, IconButton, Input, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Spacer, Spinner, Stack,
  Text, Textarea, useColorModeValue, useDisclosure, useToast,
} from '@chakra-ui/react';
import { FiPlus, FiX } from 'react-icons/fi';
import axios from 'axios';
import { useQuery } from 'react-query';
import { RecurrenceFrequency, RecurringRevenue } from '../models/recurringRevenue';
import { money } from '../utils/money';
import useRecurringRevenueActions, { EditRecurringLineInput } from '../hooks/useRecurringRevenueActions';
import { useRecurringEarners } from '../hooks/useRecurringEarners';
import useUsers from '../hooks/useUsers';
import EditRecurringEarnersModal from './EditRecurringEarnersModal';

// item 1422 (Chad: "is there a way to preview the stripe invoice using their api… confirm the copy
// that is going to be stamped") — Stripe's OWN preview of the NEXT invoice (create_preview) plus the
// memo/footer it will carry after Basix's stamp rule (Stripe's own memo wins; empty → Basix label).
interface StripeNextInvoicePreview {
  available: boolean;
  reason?: string;
  amountDue?: number;
  nextInvoiceDate?: string | null;
  collectionMethod?: string | null;
  lines?: { description?: string | null; amount: number; periodStart?: string | null; periodEnd?: string | null }[];
  memo?: string | null;
  memoSource?: 'stripe' | 'basix' | 'none';
  footer?: string | null;
  footerSource?: 'stripe' | 'basix' | 'none';
}

const sourceScheme = (src?: string): string => {
  if (src === 'stripe') return 'purple';
  if (src === 'basix') return 'green';
  return 'gray';
};

const sourceLabel = (src?: string): string => {
  if (src === 'stripe') return "Stripe's own memo";
  if (src === 'basix') return 'Stamped by Basix';
  return 'Nothing will print';
};

const shortDate = (iso?: string | null): string => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const StripeNextInvoice: React.FC<{ recurringId: string }> = ({ recurringId }) => {
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const pre = useColorModeValue('gray.50', 'whiteAlpha.100');
  const { data, isLoading } = useQuery(['stripe-next-invoice', recurringId], async () => {
    const r = await axios.get(`${process.env.REACT_APP_BASIX_API || ''}payment/subscription-preview/${recurringId}`);
    return r.data as StripeNextInvoicePreview;
  }, { staleTime: 60_000 });

  return (
    <Box borderWidth="1px" borderColor={border} borderRadius="md" p={3}>
      <HStack mb={2}>
        <Text fontSize="xs" fontWeight="bold" letterSpacing="0.06em" color={sub}>NEXT INVOICE AT STRIPE</Text>
        <Spacer />
        {isLoading && <Spinner size="xs" />}
      </HStack>
      {!isLoading && data && !data.available && (
        <Text fontSize="sm" color={sub}>{data.reason ?? 'No preview available.'}</Text>
      )}
      {data?.available && (
        <Stack spacing={2} fontSize="sm">
          <HStack>
            <Text color={sub}>{`Drafts ${shortDate(data.nextInvoiceDate)}`}</Text>
            <Spacer />
            <Text fontWeight="bold">{money(data.amountDue ?? 0)}</Text>
          </HStack>
          {(data.lines ?? []).map((l) => (
            <HStack key={`${l.description ?? ''}-${l.periodStart ?? ''}-${l.amount}`} fontSize="xs" color={sub}>
              <Text noOfLines={1}>{l.description ?? '—'}</Text>
              <Spacer />
              <Text whiteSpace="nowrap">{money(l.amount)}</Text>
            </HStack>
          ))}
          <Box>
            <HStack mb={1}>
              <Text fontSize="xs" fontWeight="bold" color={sub}>MEMO</Text>
              <Badge fontSize="0.6rem" colorScheme={sourceScheme(data.memoSource)}>
                {sourceLabel(data.memoSource)}
              </Badge>
            </HStack>
            <Box as="pre" fontSize="xs" whiteSpace="pre-wrap" fontFamily="inherit" bg={pre} p={2} borderRadius="sm">
              {data.memo || '—'}
            </Box>
          </Box>
          <Box>
            <HStack mb={1}>
              <Text fontSize="xs" fontWeight="bold" color={sub}>FOOTER</Text>
              <Badge fontSize="0.6rem" colorScheme={sourceScheme(data.footerSource)}>
                {sourceLabel(data.footerSource)}
              </Badge>
            </HStack>
            <Box as="pre" fontSize="xs" whiteSpace="pre-wrap" fontFamily="inherit" bg={pre} p={2} borderRadius="sm">
              {data.footer || '—'}
            </Box>
          </Box>
          <Text fontSize="xs" color={sub}>
            Straight from Stripe&apos;s invoice preview. A memo typed on the subscription in Stripe always wins;
            when Stripe has none, Basix stamps the project label and client PO above. Save a PO here and it shows up here on the next open.
          </Text>
        </Stack>
      )}
    </Box>
  );
};

interface EditRecurringRevenueModalProps {
  isOpen: boolean;
  onClose: () => void;
  recurring?: RecurringRevenue;
}

// item 170 — edit a recurring schedule go-forward (name / amount / cadence / service).
const EditRecurringRevenueModal: React.FC<EditRecurringRevenueModalProps> = ({
  isOpen, onClose, recurring,
}) => {
  const { update, isUpdating } = useRecurringRevenueActions();
  const toast = useToast();

  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState(RecurrenceFrequency.Monthly);
  const [customDays, setCustomDays] = useState(30);
  const [serviceCategory, setServiceCategory] = useState('');
  // item 746 (Chad) — the client's PO can be added/changed after enrollment
  const [customerPo, setCustomerPo] = useState('');
  // item 1424 (Chad: "no way to edit huh") — the invoice memo/footer notes, editable after enrollment
  const [headerNote, setHeaderNote] = useState('');
  const [footerNote, setFooterNote] = useState('');
  const earnersModal = useDisclosure();
  const { data: earners } = useRecurringEarners(isOpen ? recurring?.id : undefined);
  const { data: users } = useUsers();
  const [outsidePct, setOutsidePct] = useState('');
  const [insidePct, setInsidePct] = useState('');
  const panelEdge = useColorModeValue('blackAlpha.200', 'whiteAlpha.300'); // item 958 — light-mode contrast
  // item 731 (Chad) — the editable line breakdown (change amounts / add a new invoiced line /
  // remove one). Empty = a legacy converted-from-invoice schedule that edits the single amount.
  const [lines, setLines] = useState<EditRecurringLineInput[]>([]);

  useEffect(() => {
    if (isOpen && recurring) {
      setName(recurring.name ?? '');
      setAmount(String(recurring.amount ?? ''));
      setFrequency(recurring.frequency);
      setCustomDays(recurring.customIntervalDays ?? 30);
      setServiceCategory(recurring.serviceCategory ?? '');
      setCustomerPo(recurring.customerPoNumber ?? ''); // item 746
      setHeaderNote(recurring.invoiceHeaderNote ?? ''); // item 1424
      setFooterNote(recurring.invoiceFooterNote ?? '');
      setOutsidePct(recurring.outsideRepPercent != null ? String(recurring.outsideRepPercent) : '');
      setInsidePct(recurring.insideRepPercent != null ? String(recurring.insideRepPercent) : '');
      setLines((recurring.items ?? []).map((li) => ({
        sku: li.sku,
        brand: li.brand,
        description: li.description ?? '',
        monthlyAmount: li.monthlyAmount ?? 0,
      })));
    }
  }, [isOpen, recurring]);

  if (!recurring) return null;

  const hasLines = lines.length > 0;
  const linesTotal = lines.reduce((s, l) => s + (Number(l.monthlyAmount) || 0), 0);
  const setLine = (idx: number, patch: Partial<EditRecurringLineInput>) => setLines(
    (prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
  );
  const addLine = () => setLines((prev) => [...prev, { description: '', monthlyAmount: 0 }]);
  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));
  // item 731 (Chad) — a legacy single-amount sub (no stored line items) can be BROKEN into an
  // editable breakdown: seed one line from the current amount, then add/edit more. Once lines
  // exist the total drives the amount + the going-forward Stripe price update.
  const breakIntoLines = () => setLines([
    { description: name.trim() || 'Subscription', monthlyAmount: parseFloat(amount) || 0 },
  ]);

  const onSave = async () => {
    // item 731 — when the sub has a line breakdown, the new total drives the amount; otherwise
    // the single-amount field is authoritative (legacy converted-from-invoice schedules).
    const cleanLines = lines
      .map((l) => ({ ...l, monthlyAmount: Number(l.monthlyAmount) || 0 }))
      .filter((l) => l.monthlyAmount > 0 && (l.description.trim() || (l.sku ?? '').trim()));
    const value = hasLines ? linesTotal : parseFloat(amount);
    if (!Number.isFinite(value) || value <= 0) {
      toast({
        status: 'error',
        title: hasLines ? 'Add at least one line with an amount.' : 'Enter an amount greater than 0.',
      });
      return;
    }
    try {
      await update({
        id: recurring.id,
        name: name.trim() || recurring.name,
        amount: value,
        frequency,
        customIntervalDays: frequency === RecurrenceFrequency.Custom ? customDays : null,
        serviceCategory: serviceCategory.trim() || undefined,
        // item 746 — always send (edited or not): '' clears, value sets; backend no-ops when unchanged
        customerPoNumber: customerPo.trim(),
        invoiceHeaderNote: headerNote.trim(), // item 1424
        invoiceFooterNote: footerNote.trim(),
        // item 169 — blank → keep the current %
        outsideRepPercent: outsidePct === '' ? null : parseFloat(outsidePct),
        insideRepPercent: insidePct === '' ? null : parseFloat(insidePct),
        // item 731 — send the edited breakdown; the backend pushes a going-forward-only Stripe
        // price update for a live subscription.
        lines: hasLines ? cleanLines : undefined,
      });
      toast({ status: 'success', title: 'Recurring schedule updated.' });
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.errors?.[0] ?? e?.response?.data ?? 'Could not update the schedule.';
      toast({ status: 'error', title: 'Update failed', description: String(msg) });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit recurring schedule</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel fontSize="sm">Name</FormLabel>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </FormControl>
            {/* item 725/731 (Chad) — AMOUNT as an EDITABLE breakdown of the client's choices when the
                subscription has line items: change a line's monthly amount, add a new invoiced line,
                or remove one. The new total drives Basix and (for a live Stripe sub) a
                GOING-FORWARD-ONLY price change. Legacy converted-from-invoice schedules keep the
                single editable amount. */}
            {hasLines ? (
              <FormControl>
                <FormLabel fontSize="sm">Monthly breakdown</FormLabel>
                <Box borderWidth="1px" borderColor={panelEdge} borderRadius="md" p={3}>
                  <Stack spacing={2}>
                    {lines.map((li, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <HStack key={i} spacing={2}>
                        <Input
                          size="sm"
                          flex="1"
                          placeholder="Line description"
                          value={li.description}
                          onChange={(e) => setLine(i, { description: e.target.value })}
                        />
                        <HStack w="130px" spacing={1}>
                          <Text color="gray.500">$</Text>
                          <Input
                            size="sm"
                            type="number"
                            step="0.01"
                            min={0}
                            textAlign="right"
                            placeholder="/mo"
                            value={li.monthlyAmount || ''}
                            onChange={(e) => setLine(i, { monthlyAmount: parseFloat(e.target.value) || 0 })}
                          />
                        </HStack>
                        <IconButton
                          aria-label="Remove line"
                          size="sm"
                          variant="ghost"
                          icon={<FiX />}
                          onClick={() => removeLine(i)}
                        />
                      </HStack>
                    ))}
                    <Button size="xs" variant="outline" leftIcon={<FiPlus />} alignSelf="flex-start" onClick={addLine}>
                      Add line item
                    </Button>
                    <Divider my={1} />
                    <HStack fontSize="sm" fontWeight="bold">
                      <Text>Total monthly</Text>
                      <Spacer />
                      <Text>{`${money(linesTotal)}/mo`}</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">
                      Price changes apply going forward only — the client is never back-charged.
                    </Text>
                  </Stack>
                </Box>
              </FormControl>
            ) : (
              <FormControl>
                <FormLabel fontSize="sm">Amount</FormLabel>
                <Input type="number" step="0.01" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
                <Button size="xs" variant="outline" leftIcon={<FiPlus />} mt={2} onClick={breakIntoLines}>
                  Break into line items
                </Button>
              </FormControl>
            )}
            <FormControl>
              <FormLabel fontSize="sm">Frequency</FormLabel>
              <Select value={frequency} onChange={(e) => setFrequency(Number(e.target.value) as RecurrenceFrequency)}>
                <option value={RecurrenceFrequency.Monthly}>Monthly</option>
                <option value={RecurrenceFrequency.Quarterly}>Quarterly</option>
                <option value={RecurrenceFrequency.Annual}>Annual</option>
                <option value={RecurrenceFrequency.Custom}>Custom</option>
              </Select>
            </FormControl>
            {frequency === RecurrenceFrequency.Custom && (
              <FormControl>
                <FormLabel fontSize="sm">Interval (days)</FormLabel>
                <Input type="number" min={1} value={customDays} onChange={(e) => setCustomDays(Number(e.target.value))} />
              </FormControl>
            )}
            <FormControl>
              <FormLabel fontSize="sm">Service (optional)</FormLabel>
              <Input placeholder="Service category" value={serviceCategory} onChange={(e) => setServiceCategory(e.target.value)} />
            </FormControl>
            {/* item 746 (Chad) — the client's PO often arrives AFTER enrollment: set it here and
                every FUTURE monthly invoice's memo + footer carry it (label re-pushed to Stripe). */}
            <FormControl>
              <FormLabel fontSize="sm">Client PO # (optional)</FormLabel>
              <Input placeholder="printed on every monthly invoice" value={customerPo} onChange={(e) => setCustomerPo(e.target.value)} />
            </FormControl>
            {/* item 1424 (Chad: "no way to edit huh") — the memo/footer NOTES. Header note prints
                above the project label in the memo; footer note below it in the footer. Saved to
                Stripe metadata; the next drafted invoice picks them up (Stripe's own memo still wins). */}
            <FormControl>
              <FormLabel fontSize="sm">Invoice memo note (optional) — prints ABOVE the Basix project label; never replaces it</FormLabel>
              <Textarea rows={3} placeholder="e.g. your customer number, contract name — added above the project label" value={headerNote} onChange={(e) => setHeaderNote(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Invoice footer note (optional) — prints BELOW the Basix project label in the footer</FormLabel>
              <Textarea rows={2} placeholder="e.g. remittance instructions — added below the project label" value={footerNote} onChange={(e) => setFooterNote(e.target.value)} />
            </FormControl>
            {/* item 1422 — what the NEXT Stripe invoice will say, from Stripe's own preview */}
            {isOpen && recurring.stripeSubscriptionId && recurring.stripeSubscriptionId.startsWith('sub_') && (
              <StripeNextInvoice recurringId={recurring.id} />
            )}
            {/* item 1424 (Chad: "tom has a sub there as a rr commission. why is that not showing up
                there… what do those 4 empty fields do") — the commission split IS the earners set
                (item 448 / dart #25); the two legacy rep-% slots are shown only when a schedule still
                uses them (none live today). */}
            <Box borderWidth="1px" borderColor={panelEdge} borderRadius="md" p={3}>
              <HStack mb={1}>
                <Text fontSize="xs" fontWeight="bold" letterSpacing="0.06em" opacity={0.7}>RECURRING COMMISSION</Text>
                <Spacer />
                <Button size="xs" variant="outline" onClick={earnersModal.onOpen}>Edit commission split</Button>
              </HStack>
              {(earners ?? []).length === 0 && <Text fontSize="sm" opacity={0.7}>Nobody earns on this schedule yet.</Text>}
              {(earners ?? []).map((e) => {
                const who = ((users as any)?.index ?? []).find((u: any) => u.id === e.userId);
                return (
                  <HStack key={`${e.userId}-${e.recurringRevenueItemId ?? 'all'}`} fontSize="sm">
                    <Text>{who?.name ?? 'Team member'}</Text>
                    <Text opacity={0.7} noOfLines={1}>{e.recurringRevenueItemId ? '· on one line' : '· whole subscription'}</Text>
                    <Spacer />
                    <Text fontWeight="semibold">{`${e.percent}%`}</Text>
                  </HStack>
                );
              })}
            </Box>
            {(recurring.outsideRepUserId || recurring.insideRepUserId) && (
              <>
                {/* item 169 — edit the per-rep monthly commission %; past accruals are unaffected.
                    item 725 (Chad) — Outside rep = IO Sales · Inside rep = Sales PO (item-449 mapping). */}
                <FormControl>
                  <FormLabel fontSize="sm">
                    IO Sales commission %
                    {recurring.outsideRepName ? ` — ${recurring.outsideRepName}` : ''}
                  </FormLabel>
                  <Input type="number" step="0.01" min={0} value={outsidePct} onChange={(e) => setOutsidePct(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">
                    Sales PO commission %
                    {recurring.insideRepName ? ` — ${recurring.insideRepName}` : ''}
                  </FormLabel>
                  <Input type="number" step="0.01" min={0} value={insidePct} onChange={(e) => setInsidePct(e.target.value)} />
                </FormControl>
              </>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>Cancel</Button>
          <Button onClick={onSave} isLoading={isUpdating} loadingText="Saving">Save changes</Button>
        </ModalFooter>
      </ModalContent>
      <EditRecurringEarnersModal
        isOpen={earnersModal.isOpen}
        onClose={earnersModal.onClose}
        recurringId={recurring.id}
        recurringName={recurring.name}
      />
    </Modal>
  );
};

EditRecurringRevenueModal.defaultProps = { recurring: undefined };

export default EditRecurringRevenueModal;
